import { useState } from "react";
import { Plus, X } from "lucide-react";
import {
  DEFAULT_TAGS,
  useConversationAnnotations,
} from "@/hooks/conversation/useConversationAnnotations";

interface ConversationTagPickerProps {
  conversationId: string;
}

/**
 * Tag chips for the conversation. Default tags surface as quick-picks,
 * custom tags allowed via the inline + input. Stored in the annotations
 * hook (localStorage phase 1).
 *
 * Inline-card chrome — designed to slot into a parent card (the genie
 * identity strip) without its own gradient border.
 */
export function ConversationTagPicker({ conversationId }: ConversationTagPickerProps) {
  const { annotations, addTag, removeTag } = useConversationAnnotations(conversationId);
  const [adding, setAdding] = useState(false);
  const [customTag, setCustomTag] = useState("");

  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = customTag.trim();
    if (!t) return;
    addTag(t);
    setCustomTag("");
    setAdding(false);
  };

  const availableQuickPicks = DEFAULT_TAGS.filter((t) => !annotations.tags.includes(t));

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {/* Current tags */}
      {annotations.tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-semibold"
          style={{
            background: "linear-gradient(90deg, rgba(78,156,255,0.12), rgba(255,77,184,0.12))",
            border: "1px solid rgba(78,156,255,0.3)",
            color: "#222B45",
          }}
        >
          {tag}
          <button
            type="button"
            onClick={() => removeTag(tag)}
            className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-white/60 transition-colors"
            aria-label={`Remove tag ${tag}`}
          >
            <X className="w-2.5 h-2.5 text-gray-500" />
          </button>
        </span>
      ))}

      {/* Quick-pick chips */}
      {availableQuickPicks.map((tag) => (
        <button
          key={tag}
          type="button"
          onClick={() => addTag(tag)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-dashed text-gray-600 hover:text-gray-900 hover:border-solid transition-all"
          style={{ borderColor: "rgba(78,156,255,0.4)" }}
        >
          <Plus className="w-2.5 h-2.5" />
          {tag}
        </button>
      ))}

      {/* Custom tag input */}
      {adding ? (
        <form onSubmit={handleCustomSubmit} className="inline-flex items-center gap-1">
          <input
            autoFocus
            type="text"
            value={customTag}
            onChange={(e) => setCustomTag(e.target.value)}
            onBlur={() => {
              if (!customTag.trim()) setAdding(false);
            }}
            placeholder="Custom tag…"
            maxLength={32}
            className="px-2.5 py-1 rounded-full text-xs border border-genie-primary/40 focus:outline-none focus:ring-2 focus:ring-genie-primary/30 bg-white w-32"
          />
          <button
            type="submit"
            className="text-xs font-semibold bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent"
          >
            Add
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center justify-center w-6 h-6 rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          aria-label="Add custom tag"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
